import axios from "axios";
import { API_BASE as SBIN_API_BASE } from "./base";

const API = SBIN_API_BASE;

function authHeaders() {
  const token = null;
  return token
    ? {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    : { "Content-Type": "application/json" };
}

function authHeadersWithSalon(salonId) {
  const headers = authHeaders();
  if (salonId !== null && salonId !== undefined && salonId !== "") {
    return { ...headers, "X-Salon-Id": String(salonId) };
  }
  return headers;
}

function authHeadersMultipart() {
  const token = null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function downloadBlob(blob, filename) {
  let finalBlob = blob;
  if ((filename || "").toLowerCase().endsWith(".csv")) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    finalBlob = new Blob([bom, blob], { type: "text/csv;charset=utf-8" });
  }
  const url = window.URL.createObjectURL(finalBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "total_sheet.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function extractFilename(headers) {
  const disposition = headers?.["content-disposition"] || "";
  const match = disposition.match(/filename=([^;]+)/i);
  if (!match) return "";
  return match[1].replace(/"/g, "");
}

function openHtmlInNewTab(html, targetWindow) {
  const blob = new Blob([html || ""], { type: "text/html;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);

  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = url;
  } else {
    window.open(url, "_blank");
  }

  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

export async function getTotalSheetSummary({ start, end }) {
  const res = await axios.get(`${API}/reports/total-sheet/summary`, {
    params: { start, end },
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportTotalSheetNow({
  start,
  end,
  format,
  name,
  targetWindow,
}) {
  const payload = {
    start,
    end,
    format,
    name: name || null,
    mode: "now",
  };

  if (String(format).toLowerCase() === "pdf") {
    const res = await axios.post(`${API}/reports/total-sheet/export`, payload, {
      headers: authHeaders(),
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    return { ok: true };
  }

  if (String(format).toLowerCase() === "csv") {
    const res = await axios.post(`${API}/reports/total-sheet/export`, payload, {
      headers: authHeaders(),
      responseType: "blob",
    });
    const filename = extractFilename(res.headers) || "total_sheet.csv";
    downloadBlob(res.data, filename);
    return { ok: true };
  }

  const res = await axios.post(`${API}/reports/total-sheet/export`, payload, {
    headers: authHeaders(),
    responseType: "text",
  });
  openHtmlInNewTab(res.data, targetWindow);
  return { ok: true };
}

export async function createTotalSheetJob({ start, end, format, name }) {
  const res = await axios.post(
    `${API}/reports/total-sheet/export`,
    {
      start,
      end,
      format,
      name: name || null,
      mode: "queue",
    },
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function getReportJob(jobId) {
  const res = await axios.get(`${API}/reports/jobs/${jobId}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function downloadReportJobFile(jobId) {
  if (!jobId) {
    throw new Error("JOB_ID_REQUIRED");
  }
  const res = await axios.get(`${API}/reports/jobs/${jobId}/download`, {
    headers: authHeaders(),
    responseType: "blob",
  });
  const disposition = res.headers?.["content-disposition"] || "";
  let filename = "report";
  const match = disposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  );
  if (match) {
    const raw = match[1] || match[2] || filename;
    try {
      filename = decodeURIComponent(raw);
    } catch (error) {
      filename = raw;
    }
  } else {
    const contentType = res.headers?.["content-type"] || "";
    if (contentType.includes("pdf")) {
      filename = "report.pdf";
    } else if (contentType.includes("csv")) {
      filename = "report.csv";
    } else if (contentType.includes("html")) {
      filename = "report.html";
    }
  }

  return { blob: res.data, filename, contentType: res.headers?.["content-type"] };
}

export async function openReportJobDownload(jobId) {
  const { blob, filename } = await downloadReportJobFile(jobId);
  downloadBlob(blob, filename);
}

export async function getCancellationAnalytics(payload) {
  const res = await axios.post(`${API}/reports/cancellations/analytics`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportCancellationAnalytics(payload) {
  const res = await axios.post(
    `${API}/reports/cancellations/analytics/export`,
    payload,
    {
      headers: authHeaders(),
      responseType: "blob",
    }
  );
  const filename =
    extractFilename(res.headers) || "cancellations_report.csv";
  downloadBlob(res.data, filename);
}

function buildTaxLiabilityFilename(kind, start, end) {
  const clean = (value) => (value || "").replaceAll("-", "");
  const startToken = clean(start);
  const endToken = clean(end);
  const range =
    startToken && endToken ? `${startToken}-${endToken}` : "range";
  const safeKind = kind || "detail";
  return `tax_liability_${safeKind}_${range}.csv`;
}

function buildTaxLiabilityParams({
  start,
  end,
  employeeIds,
  showTaxable,
  showExempt,
  showTaxableItems,
  showTaxExemptItems,
  onlyExempt,
  onlyTaxableItems,
  payByCash,
}) {
  const params = {
    start,
    end,
  };
  if (payByCash) {
    params.pay_by_cash = true;
  }
  const showTaxableValue =
    typeof showTaxable === "boolean"
      ? showTaxable
      : typeof showTaxableItems === "boolean"
      ? showTaxableItems
      : undefined;
  const showExemptValue =
    typeof showExempt === "boolean"
      ? showExempt
      : typeof showTaxExemptItems === "boolean"
      ? showTaxExemptItems
      : undefined;

  if (typeof showTaxableValue === "boolean") {
    params.show_taxable = showTaxableValue;
  }
  if (typeof showExemptValue === "boolean") {
    params.show_exempt = showExemptValue;
  }

  const legacyShowTaxable =
    typeof showTaxableValue === "boolean"
      ? showTaxableValue
      : typeof onlyTaxableItems === "boolean"
      ? onlyTaxableItems
      : undefined;
  const legacyShowExempt =
    typeof showExemptValue === "boolean"
      ? showExemptValue
      : typeof onlyExempt === "boolean"
      ? onlyExempt
      : undefined;

  if (legacyShowTaxable && !legacyShowExempt) {
    params.only_taxable = true;
  }
  if (legacyShowExempt && !legacyShowTaxable) {
    params.only_exempt = true;
  }
  if (Array.isArray(employeeIds) && employeeIds.length > 0) {
    params.employee_ids = employeeIds;
  }
  return params;
}

export async function getTaxLiabilitySummary({
  start,
  end,
  employeeIds,
  showTaxableItems,
  showTaxExemptItems,
  payByCash,
}) {
  const res = await axios.get(`${API}/reports/tax-liability/summary`, {
    params: buildTaxLiabilityParams({
      start,
      end,
      employeeIds,
      showTaxableItems,
      showTaxExemptItems,
      payByCash,
    }),
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTaxLiabilityDetail({
  start,
  end,
  employeeIds,
  showTaxableItems,
  showTaxExemptItems,
  payByCash,
}) {
  const res = await axios.get(`${API}/reports/tax-liability/detail`, {
    params: buildTaxLiabilityParams({
      start,
      end,
      employeeIds,
      showTaxableItems,
      showTaxExemptItems,
      payByCash,
    }),
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportTaxLiability({
  start,
  end,
  employeeIds,
  showTaxableItems,
  showTaxExemptItems,
  payByCash,
  kind,
  format,
  targetWindow,
}) {
  const showTaxableValue = !!showTaxableItems;
  const showExemptValue = !!showTaxExemptItems;
  const payload = {
    start,
    end,
    kind,
    format,
    employee_ids: Array.isArray(employeeIds) ? employeeIds : [],
    show_taxable: showTaxableValue,
    show_exempt: showExemptValue,
    pay_by_cash: !!payByCash,
  };
  if (showTaxableValue && !showExemptValue) {
    payload.only_taxable = true;
  }
  if (showExemptValue && !showTaxableValue) {
    payload.only_exempt = true;
  }

  const responseType = String(format).toLowerCase() === "pdf" ? "blob" : "blob";
  const res = await axios.post(`${API}/reports/tax-liability/export`, payload, {
    headers: authHeaders(),
    responseType,
  });

  if (String(format).toLowerCase() === "pdf") {
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    return { ok: true };
  }

  const filename = buildTaxLiabilityFilename(kind, start, end);
  downloadBlob(res.data, filename);
  return { ok: true };
}

export async function getDailyClosingPrefill({ date }) {
  const res = await axios.get(`${API}/reports/daily-closing/prefill`, {
    params: { date },
    headers: authHeaders(),
  });
  return res.data;
}

export async function listDailyClosingReports({ start, end }) {
  const res = await axios.get(`${API}/reports/daily-closing`, {
    params: { start, end },
    headers: authHeaders(),
  });
  return res.data;
}

export async function getDailyClosingReport(id) {
  const res = await axios.get(`${API}/reports/daily-closing/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getDailyClosingReportByWorkDate(work_date) {
  if (!work_date) return null;
  try {
    return await getDailyClosingReportByDate({ work_date });
  } catch (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 404 && detail === "DAILY_CLOSING_REPORT_NOT_FOUND") {
      return null;
    }
    throw error;
  }
}

export async function getDailyClosingReportByDate({ work_date }) {
  const res = await axios.get(`${API}/reports/daily-closing/by-date`, {
    params: { work_date },
    headers: authHeaders(),
  });
  return res.data;
}

export async function upsertDailyClosingReport(payload) {
  const res = await axios.post(`${API}/reports/daily-closing`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function uploadDailyClosingReceipt(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${API}/reports/daily-closing/receipts`, formData, {
    headers: authHeadersMultipart(),
  });
  return res.data;
}

export async function getCashBalanceReport({ start, end }) {
  const res = await axios.get(`${API}/reports/cash-balance`, {
    params: { start, end },
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportCashBalanceReport({ start, end, filename }) {
  const res = await axios.get(`${API}/reports/cash-balance/export`, {
    params: { start, end },
    headers: authHeaders(),
    responseType: "blob",
  });
  const fallbackName = `cash_balance_report_${start}_to_${end}.csv`;
  downloadBlob(res.data, filename || fallbackName);
  return { ok: true };
}

export async function getSalesByEmployeeReport(payload) {
  const res = await axios.post(`${API}/reports/sales-by-employee`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportSalesByEmployeeCsv(payload) {
  const res = await axios.post(
    `${API}/reports/sales-by-employee/export`,
    payload,
    {
      headers: authHeaders(),
      responseType: "blob",
    }
  );
  const start = payload?.start_date || "start";
  const end = payload?.end_date || "end";
  const fallbackName = `sales_by_employee_${start}_to_${end}.csv`;
  downloadBlob(res.data, fallbackName);
  return { ok: true };
}

export async function getSalesTicketsReport(payload) {
  const res = await axios.post(`${API}/reports/sales-tickets`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getSalesTicketsTopTimeRankings(payload) {
  const res = await axios.post(
    `${API}/reports/sales-tickets/top-time-rankings`,
    payload,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function exportSalesTicketsReport(payload) {
  const res = await axios.post(`${API}/reports/sales-tickets/export`, payload, {
    headers: authHeaders(),
    responseType: "blob",
  });
  const start = payload?.start_date || "start";
  const end = payload?.end_date || "end";
  const fallbackName = `sales_tickets_${start}_to_${end}.zip`;
  downloadBlob(res.data, fallbackName);
  return { ok: true };
}

export async function getTicketSummaryReport(params) {
  const res = await axios.get(`${API}/reports/tickets/summary`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportTicketSummaryCsv(params) {
  const res = await axios.get(`${API}/reports/tickets/summary/export`, {
    params,
    headers: authHeaders(),
    responseType: "blob",
  });
  const fallbackName = "ticket_summary.csv";
  const filename = extractFilename(res.headers) || fallbackName;
  downloadBlob(res.data, filename);
  return { ok: true };
}

export async function getStaffPerformanceReport(payload) {
  const res = await axios.post(`${API}/reports/staff-performance`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getLiveDashboard(payload, token = null) {
  const headers = token
    ? {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    : authHeaders();
  const res = await axios.post(`${API}/reports/live-dashboard`, payload, {
    headers,
  });
  return res.data;
}

export async function exportStaffPerformanceCsv(payload) {
  const res = await axios.post(
    `${API}/reports/staff-performance/export`,
    payload,
    {
      headers: authHeaders(),
      responseType: "blob",
    }
  );
  const start = payload?.start_date || "start";
  const end = payload?.end_date || "end";
  const fallbackName = `staff_performance_${start}_to_${end}.csv`;
  downloadBlob(res.data, fallbackName);
  return { ok: true };
}

export async function getTrainingProgressSummary(params) {
  const res = await axios.get(`${API}/reports/training/progress/summary`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTrainingProgressDetails(params) {
  const res = await axios.get(`${API}/reports/training/progress/details`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportTrainingProgressCsv(params) {
  const res = await axios.get(`${API}/reports/training/progress/export`, {
    params,
    headers: authHeaders(),
    responseType: "blob",
  });
  const filename =
    extractFilename(res.headers) ||
    `training_progress_${params?.date_from || "start"}_${params?.date_to || "end"}.csv`;
  downloadBlob(res.data, filename);
  return { ok: true };
}

export async function getGiftCardsCashDrawerReport(payload) {
  const res = await axios.post(`${API}/reports/gift-cards-cash-drawer`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function exportGiftCardsCashDrawerReport(payload) {
  const res = await axios.post(`${API}/reports/gift-cards-cash-drawer/export`, payload, {
    headers: authHeaders(),
    responseType: "blob",
  });
  const start = payload?.start_date || "start";
  const end = payload?.end_date || "end";
  const fallbackName = `gift_cards_cash_drawer_${start}_to_${end}.zip`;
  downloadBlob(res.data, fallbackName);
  return { ok: true };
}

export async function getServiceTotalsReport(params) {
  const res = await axios.get(`${API}/reports/service-totals`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceMixByEmployeeReport(params) {
  const res = await axios.get(`${API}/reports/service-mix-by-employee`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTopServicesReport(params) {
  const res = await axios.get(`${API}/reports/top-services`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceAnalyticsOverview(params) {
  const res = await axios.get(`${API}/reports/service-analytics/overview`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceTotals(params) {
  const res = await axios.get(`${API}/reports/service-totals`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceMixByEmployee(params) {
  const res = await axios.get(`${API}/reports/service-mix-by-employee`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTopServices(params) {
  const res = await axios.get(`${API}/reports/top-services`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getClientsByService(params) {
  const res = await axios.get(`${API}/reports/clients-by-service`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceRetention(params) {
  const res = await axios.get(`${API}/reports/service-retention`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceCombos(params) {
  const res = await axios.get(`${API}/reports/service-combos`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceDiscountIntegrity(params) {
  const res = await axios.get(`${API}/reports/service-discount-integrity`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceRefunds(params) {
  const res = await axios.get(`${API}/reports/service-refunds`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getServiceCapacity(params) {
  const res = await axios.get(`${API}/reports/service-capacity`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function postCategoryRevenueByCategory(body) {
  const res = await axios.post(
    `${API}/reports/service-category/revenue-by-category`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postCategoryMixOverTime(body) {
  const res = await axios.post(
    `${API}/reports/service-category/mix-over-time`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postCategoryStaffingInsights(body) {
  const res = await axios.post(
    `${API}/reports/service-category/staffing-insights`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postCategoryGrowth(body) {
  const res = await axios.post(
    `${API}/reports/service-category/growth`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postCategoryConcentration(body) {
  const res = await axios.post(
    `${API}/reports/service-category/concentration`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postCategorySeasonality(body) {
  const res = await axios.post(
    `${API}/reports/service-category/seasonality`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postGiftCardsIssued(body) {
  const res = await axios.post(`${API}/reports/gift-cards/issued`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function postGiftCardsBalancesAsOf(body) {
  const res = await axios.post(`${API}/reports/gift-cards/balances-as-of`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function postGiftCardsClientsWithBalance(body) {
  const res = await axios.post(
    `${API}/reports/gift-cards/clients-with-balance`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function postGiftCardsSummary(body) {
  const res = await axios.post(`${API}/reports/gift-cards/summary`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getAppointmentStatusReport(payload) {
  const res = await axios.post(`${API}/reports/appointments/status-list`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getProfitabilityEstimateConfig({ salonId }) {
  const res = await axios.get(`${API}/reports/profitability-estimate/config`, {
    headers: authHeadersWithSalon(salonId),
  });
  return res.data;
}

export async function getProfitabilityEstimateContext({ salonId }) {
  const res = await axios.get(`${API}/reports/profitability-estimate/context`, {
    headers: authHeadersWithSalon(salonId),
  });
  return res.data;
}

export async function saveProfitabilityEstimateConfig({ salonId, items }) {
  const res = await axios.post(
    `${API}/reports/profitability-estimate/config`,
    { items },
    {
      headers: authHeadersWithSalon(salonId),
    }
  );
  return res.data;
}

export async function runProfitabilityEstimate({ salonId, start_date, end_date }) {
  const res = await axios.post(
    `${API}/reports/profitability-estimate/run`,
    { start_date, end_date },
    {
      headers: authHeadersWithSalon(salonId),
    }
  );
  return res.data;
}

export async function getProfitabilityEstimateRevenue({
  salonId,
  start_date,
  end_date,
}) {
  const res = await axios.post(
    `${API}/reports/profitability-estimate/revenue`,
    { start_date, end_date },
    {
      headers: authHeadersWithSalon(salonId),
    }
  );
  return res.data;
}

export async function getPayrollSummary({ salonId, start_date, end_date }) {
  const res = await axios.post(
    `${API}/reports/payroll/summary`,
    { start_date, end_date },
    {
      headers: authHeadersWithSalon(salonId),
    }
  );
  return res.data;
}

export async function getRewardsSummary({ date_from, date_to, limit_top }) {
  const res = await axios.post(
    `${API}/reports/rewards/summary`,
    { date_from, date_to, limit_top },
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function getMaintenanceReportSummary(params = {}) {
  const res = await axios.get(`${API}/reports/maintenance/summary`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getMaintenanceReportTrends(params = {}) {
  const res = await axios.get(`${API}/reports/maintenance/trends`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getMaintenanceReportTasks(params = {}) {
  const res = await axios.get(`${API}/reports/maintenance/tasks`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getMaintenanceReportStaff(params = {}) {
  const res = await axios.get(`${API}/reports/maintenance/staff`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getMaintenanceReportLogs(params = {}) {
  const res = await axios.get(`${API}/reports/maintenance/logs`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}
